import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { logError } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Plane, Globe, Star, Clock, ArrowRight, DollarSign } from "lucide-react";
import { EditableText as T } from "@/components/live/EditableText";
import { EditableList } from "@/components/live/EditableList";
import { Section } from "@/components/live/Section";

const DEFAULT_DURATION_OPTIONS = [
  { duration: "3 Hours", regular: "$1,800", pse: "$4,500" },
  { duration: "6 Hours", regular: "$3,000", pse: "$8,000" },
  { duration: "8 Hours", regular: "$4,000", pse: "$9,000" },
  { duration: "12 Hours", regular: "$6,000", pse: "$12,000" },
  { duration: "24 Hours", regular: "$8,000", pse: "$15,000" },
  { duration: "48 Hours", regular: "$10,000", pse: "$20,000" },
  { duration: "72 Hours", regular: "$12,000", pse: "$25,000" }
];
const DEFAULT_NOTE = "Note: Please inquire further regarding longer arrangements.";

const ExclusiveTravel = () => {
  const inclusions = [
    "Airfare accommodation",
    "Hotel/lodging accommodation if applicable (separate sleeping area for privacy, such as a 2-bedroom suite or adjoining rooms)",
    "Daily expenses and activities",
    "Travel insurance (50% deposit, please ensure this is handled at least two weeks prior to the arrangement dates.)"
  ];

  const [durationOptions, setDurationOptions] = useState(DEFAULT_DURATION_OPTIONS);
  const [ratesNote, setRatesNote] = useState(DEFAULT_NOTE);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const res = await axios.get(`${API}/public/travel-rates`);
        if (res.data?.rates?.length) {
          setDurationOptions(res.data.rates);
        }
        if (res.data?.note) {
          setRatesNote(res.data.note);
        }
      } catch (error) {
        logError("Failed to fetch travel rates, using defaults:", error);
      }
    };
    fetchRates();
  }, []);

  return (
    <div data-testid="exclusive-travel-page">
      {/* Header */}
      <section className="page-header" data-testid="travel-header">
        <div className="max-w-4xl mx-auto px-6">
          <T k="travel.adventure-awaits" as="p" className="font-body text-sm tracking-widest uppercase text-champagne-gold mb-4 animate-fade-in">Adventure Awaits</T>
          <T k="travel.exclusive-travel" as="h1" className="page-title animate-fade-in stagger-1">Exclusive Travel</T>
          <T k="travel.bring-me-to" as="p" multiline className="page-subtitle animate-fade-in stagger-2">Bring me to your city or wherever you are, on your terms or transform ordinary trips into extraordinary experiences with a captivating travel companion.</T>
        </div>
      </section>

      {/* Introduction */}
      <Section k="travel.section.travel-intro" label="travel intro" className="py-20 px-6 md:px-12 lg:px-24" data-testid="travel-intro">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <T k="travel.experience-life-together" as="h2" className="font-heading text-4xl text-charcoal mb-6">Experience Life Together</T>
              <T k="travel.travel-is-infinitely" as="p" multiline className="font-body text-stone-grey leading-relaxed mb-6">Travel is infinitely more enjoyable with the right companion. I bring warmth, intelligence, and an adventurous spirit to every journey, whether it's a business trip that needs a sophisticated plus-one or a leisurely vacation seeking genuine connection.</T>
              <div className="flex items-center gap-4">
                <Globe className="w-6 h-6 text-jade-text" />
                <T k="travel.available-for-us" className="font-body text-charcoal">Available for U.S. Domestic travel</T>
              </div>
            </div>
            <div className="image-frame aspect-[4/5] rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1762257284137-ac2a87379c2a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxuZXclMjB5b3JrJTIwY2l0eSUyMHNreWxpbmUlMjBzdW5zZXQlMjBzb2Z0JTIwYWVzdGhldGljfGVufDB8fHx8MTc2NzczMDAxMXww&ixlib=rb-4.1.0&q=85"
                alt="Travel destination"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* Travel Arrangements */}
      <Section k="travel.section.arrangements-section" label="arrangements" className="py-20 px-6 md:px-12 lg:px-24 bg-blush-pink/20" data-testid="arrangements-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* What's Included */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Plane className="w-6 h-6 text-jade-text" />
                <T k="travel.travel-arrangements" as="h2" className="font-heading text-3xl text-charcoal">Travel Arrangements</T>
              </div>
              <T k="travel.for-travel-companionship" as="p" className="font-body text-stone-grey leading-relaxed mb-8">For travel companionship, the following arrangements are typically expected:</T>
                              <EditableList k="travel.inclusions" items={inclusions} newItem="New inclusion" addLabel="Add inclusion" className="space-y-4">
                  {(item, index, Field) => (
                    <div className="flex items-start gap-4" data-testid={`inclusion-${index}`}>
                      <Star className="w-5 h-5 text-champagne-gold flex-shrink-0 mt-0.5" />
                      <Field className="font-body text-stone-grey" />
                    </div>
                  )}
                </EditableList>
            </div>

            {/* Duration & Donations */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-6 h-6 text-jade-text" />
                <T k="travel.duration-options" as="h2" className="font-heading text-3xl text-charcoal">Duration Options</T>
              </div>
              <div className="space-y-3">
                {/* Header Row */}
                <div className="grid grid-cols-3 gap-4 px-4 py-2">
                  <T k="travel.duration" className="font-body text-xs tracking-widest uppercase text-stone-grey">Duration</T>
                  <T k="travel.regular" className="font-body text-xs tracking-widest uppercase text-stone-grey text-center">Regular</T>
                  <T k="travel.pse" className="font-body text-xs tracking-widest uppercase text-stone-grey text-center">PSE</T>
                </div>
                {/* Duration Rows */}
                {durationOptions.map((option, index) => (
                  <div key={option.duration || index} className="glass rounded-xl p-4 grid grid-cols-3 gap-4 items-center" data-testid={`duration-option-${index}`}>
                    <h3 className="font-heading text-lg text-charcoal">{option.duration}</h3>
                    <p className="font-body text-sm text-jade-text text-center font-medium">{option.regular}</p>
                    <p className="font-body text-sm text-champagne-gold text-center font-medium">{option.pse}</p>
                  </div>
                ))}
              </div>
              <p className="font-body text-sm text-taupe mt-6 text-center italic">
                {ratesNote}
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Quote */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-jade-deep">
        <div className="max-w-4xl mx-auto text-center">
          <T k="travel.the-world-is" as="blockquote" className="font-heading text-2xl md:text-3xl text-ivory leading-relaxed italic">"The world is a book, and those who do not travel read only one page. Let's write a chapter together."</T>
        </div>
      </section>

      {/* CTA */}
      <Section k="travel.section.travel-cta" label="travel cta" className="py-20 px-6 md:px-12 lg:px-24" data-testid="travel-cta">
        <div className="max-w-4xl mx-auto text-center">
          <T k="travel.plan-your-journey" as="h2" className="font-heading text-4xl text-charcoal mb-6">Plan Your Journey</T>
          <T k="travel.ready-to-make" as="p" multiline className="font-body text-stone-grey leading-relaxed mb-12 max-w-2xl mx-auto">Ready to make your next trip unforgettable? Submit an inquiry with your destination, dates, and what you envision for our adventure together.</T>
          
          <Link to="/booking">
            <Button className="btn-primary" data-testid="travel-inquiry-btn">
              Submit Travel Inquiry
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </Section>
    </div>
  );
};

export default ExclusiveTravel;
